import React, { FC, useMemo } from 'react';
import {
  decOfNum,
  ProfileInformation,
  Typography,
  TypographyVariantsEnum,
  IconClose,
  IconPause,
  IconResume,
  Button_v2 as Button,
  priceNormalize,
  SupportedCurrency,
} from '@mindly/ui-components';
import { useSelector } from 'react-redux';
import { globalVariables } from '../../../redux/firestoreGlobalVariables/selector';
import { ISubscription } from '../../../redux/subscriptions/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { SubscriptionCardWrapper } from './SubscriptionCard.style';
import { getPlanStatus, SubscriptionStatusType } from '../../../utils/getPlanStatus';
import useLangs from '../../../context/LangsContext';
import { IonIcon, IonProgressBar } from '@ionic/react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { StripeSupportedCurrency } from 'src/redux/specialists/types';

type SubscriptionCardProps = {
  subscription: ISubscription;
  onPauseClick?: (subscription: ISubscription) => void;
  onUnPauseClick?: (subscription: ISubscription) => void;
  onCancelClick?: (subscription: ISubscription) => void;
};

const SubscriptionCard: FC<SubscriptionCardProps> = ({ subscription, onPauseClick, onCancelClick, onUnPauseClick }) => {
  const {
    therapist: specialist,
    period_end,
    period_start,
    pause_used_at,
    session_count,
    used_balance,
    next_payment_default_in_cents,
    next_payment_original_in_cents,
    exchange_rate_in_basis_points,
    charge_currency,
    session_price_type,
  } = subscription;

  const intl = useIntl();
  const { locale } = useLangs();
  const { t } = useTranslation('subscriptions');

  const globalVars = useSelector(globalVariables);

  const subscriptionStatus: SubscriptionStatusType = useMemo(() => getPlanStatus(subscription, t), [subscription, t]);

  const allDays = useMemo(() => {
    const start = DateTime.fromSeconds(period_start);
    const end = DateTime.fromSeconds(period_end);

    const diffDays = end.diff(start, 'days').toObject().days ?? 0;

    return Math.floor(diffDays);
  }, [period_start, period_end]);

  const daysLeft = useMemo(() => {
    const end = DateTime.fromSeconds(period_end);
    const toDay = DateTime.now();

    const diffDays = end.diff(toDay, 'days').toObject().days ?? 0;

    return Math.ceil(diffDays);
  }, [period_end]);

  const inactiveStatusList = useMemo(() => ['cancel', 'pause'], []);

  const pausedDaysLeft = useMemo(() => {
    if (!pause_used_at) return 0;

    const end = DateTime.fromSeconds(period_end);
    const pauseDay = DateTime.fromSeconds(pause_used_at);

    const diffDays = end.diff(pauseDay, 'days').toObject().days ?? 0;

    return Math.ceil(diffDays);
  }, [period_end, pause_used_at]);

  const activeButtons = useMemo(
    () => (
      <>
        {!pause_used_at && (
          <Button fill={'outline'} onClick={() => onPauseClick?.(subscription)} fullWidth>
            <div className="flex items-center gap-2 justify-center">
              <IconPause />
              {t('subscriptions.subscriptionControls.pauseButton')}
            </div>
          </Button>
        )}

        <Button fill={'outline'} onClick={() => onCancelClick?.(subscription)} fullWidth>
          <div className="flex items-center gap-2 justify-center">
            <IconClose />
            {t('subscriptions.subscriptionControls.cancelButton')}
          </div>
        </Button>
      </>
    ),
    [pause_used_at]
  );

  const pauseButtons = useMemo(
    () => (
      <Button fill={'outline'} onClick={() => onUnPauseClick?.(subscription)} fullWidth>
        <div className="flex items-center gap-2 justify-center">
          <IconResume />
          {t('subscriptions.subscriptionControls.resumeButton')}
        </div>
      </Button>
    ),
    []
  );

  return (
    <SubscriptionCardWrapper>
      <ProfileInformation
        className={'specialist-profile'}
        specialistAvatar={specialist?.avatar_link as string}
        specialistFirstName={specialist?.first_name}
        specialistLastName={specialist?.last_name}
        specialistSpecialization={specialist?.specialization as string}
        countryCode={specialist?.country_of_birthday as string}
        countOfReviews={
          specialist?.reviews_count >= globalVars.specialistReviewsCountForShowing
            ? specialist?.reviews_count
            : undefined
        }
        averageStarsCount={
          specialist?.reviews_count >= globalVars.specialistReviewsCountForShowing
            ? specialist?.reviews_average_rating
            : undefined
        }
        translations={{
          reviews: [
            intl.formatMessage({
              id: 'other.specialistReviews.one',
              defaultMessage: 'відгук',
            }),
            intl.formatMessage({
              id: 'other.specialistReviews.many',
              defaultMessage: 'відгуки',
            }),
            intl.formatMessage({
              id: 'other.specialistReviews.several',
              defaultMessage: 'відгуків',
            }),
          ],
        }}
      />
      <div className={`subscription-title ${subscriptionStatus.status}`}>
        <Typography variant={TypographyVariantsEnum.Text20Semi}>
          {subscription.cancel_at_period_end
            ? t('subscriptions.subscriptionStatus.subscriptionCanceled')
            : subscriptionStatus?.status === 'expiring-soon'
              ? t('subscriptions.subscriptionStatus.renewalSoon')
              : subscriptionStatus.text}
        </Typography>{' '}
        <IonIcon icon={subscriptionStatus.icon} />
      </div>
      <div className={'subscription-date-wrapper'}>
        <div className={'subscription-date'}>
          {subscriptionStatus.status === 'pause' ? (
            <>
              <FormattedMessage
                id={'subscription.progress.pause.text'}
                defaultMessage={'Після відновлення наступний платіж відбудеться через: '}
              />{' '}
              <b>
                {pausedDaysLeft}{' '}
                {decOfNum(pausedDaysLeft, [
                  intl.formatMessage({
                    id: 'other.days.one',
                    defaultMessage: 'день',
                  }),
                  intl.formatMessage({
                    id: 'other.days.many',
                    defaultMessage: 'дня',
                  }),
                  intl.formatMessage({
                    id: 'other.days.several',
                    defaultMessage: 'днів',
                  }),
                ])}
              </b>
            </>
          ) : (
            <>
              <FormattedMessage id={'general.available.to'} defaultMessage={'Діє до'} />:{' '}
              <b>
                {new Date(+period_end * 1000).toLocaleString(locale, {
                  day: 'numeric',
                  month: 'long',
                })}
              </b>
            </>
          )}
        </div>
        <IonProgressBar
          className={`subscription-date-progress ${subscriptionStatus.status}`}
          value={subscriptionStatus.status === 'pause' ? pausedDaysLeft : daysLeft / allDays}
        />
      </div>
      <div className={'subscription-balance-wrapper'}>
        <div className={'subscription-balance'}>
          <FormattedMessage id={'subscriptions.balance'} defaultMessage={'Баланс'} />:{' '}
          <b>
            {session_count - used_balance}{' '}
            {decOfNum(session_count - used_balance, [
              intl.formatMessage({
                id: 'other.sessions.one',
                defaultMessage: 'сеанс',
              }),
              intl.formatMessage({
                id: 'other.sessions.many',
                defaultMessage: 'сеанси',
              }),
              intl.formatMessage({
                id: 'other.sessions.several',
                defaultMessage: 'сеансів',
              }),
            ])}
          </b>
        </div>
        <div className={'balance-list'}>
          {new Array(session_count).fill('').map((i, index) => (
            <div key={index} className={`balance-item ${index + 1 > used_balance ? 'filled' : ''}`} />
          ))}
        </div>
      </div>
      {!inactiveStatusList.includes(subscriptionStatus?.status ?? '') && (
        <div className={'subscription-info'}>
          <div className={'subscription-date-price-info'}>
            <FormattedMessage id={'subscription.next.price.title'} defaultMessage={'Наступнне автоматичне списання'} />
            <b>
              {new Date(+period_end * 1000).toLocaleString(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </b>
          </div>
          <div className={'subscription-date-price-info'}>
            <FormattedMessage id={'subscription.next.pay.title'} defaultMessage={'Сума автоматичного списання'} />
            <b>
              {priceNormalize({
                price: next_payment_original_in_cents / 100,
                currency: session_price_type as unknown as SupportedCurrency,
                extendedUsd: true,
              })}
              {!StripeSupportedCurrency?.includes(session_price_type) && (
                <>
                  {' '}
                  (
                  {priceNormalize({
                    price: next_payment_default_in_cents / 100,
                    currency: charge_currency as unknown as SupportedCurrency,
                    extendedUsd: true,
                  })}{' '}
                  {t('subscriptions.general.exchangeRate')} {Number(exchange_rate_in_basis_points / 10000).toFixed(4)})
                </>
              )}
            </b>
          </div>
        </div>
      )}
      {subscriptionStatus.status === 'cancel' && (
        <div className={'subscription-cancel-desc'}>
          <FormattedMessage
            id={'subscription.status.cancel.description'}
            defaultMessage={'Підписку не буде оновлено на новий період, і з Вас більше не стягуватиметься плата'}
          />
        </div>
      )}
      <div className={'flex gap-2'}>
        {!inactiveStatusList.includes(subscriptionStatus?.status ?? '') && activeButtons}
        {subscriptionStatus.status === 'pause' && pauseButtons}
      </div>
    </SubscriptionCardWrapper>
  );
};

export default SubscriptionCard;
